import React from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';

interface DepartmentRequest {
  id: number;
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: string;
  user_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  asset_name: string;
  category_name: string;
  company_name: string;
  approver_name: string;
  departmentName: string;
  requestId: number;
  createdAt: string;
}

interface ViewPendingApprovalModalProps {
  show: boolean;
  handleClose: () => void;
  request: DepartmentRequest | null;
}

const ViewPendingApprovalModal: React.FC<ViewPendingApprovalModalProps> = ({
  show,
  handleClose,
  request
}) => {
  if (!request) return null;

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'success', label: 'Low' },
      medium: { variant: 'warning', label: 'Medium' },
      high: { variant: 'danger', label: 'High' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      variant: 'secondary',
      label: priority
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      cancelled: { variant: 'secondary', label: 'Cancelled' },
      completed: { variant: 'info', label: 'Completed' },
      in_progress: { variant: 'primary', label: 'In Progress' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'secondary',
      label: status
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">Request Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="container-fluid">
          {/* Request Information */}
          <div className="row mb-3">
            <div className="col-12">
              <h6 className="text-primary mb-2 border-bottom pb-2">
                Request Information
              </h6>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Asset</label>
              <div className="fs-7 fw-semibold">
                {request.asset_name || 'N/A'}
              </div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Category</label>
              <div className="fs-7">{request.category_name || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Priority</label>
              <div className="fs-0">{getPriorityBadge(request.priority)}</div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Status</label>
              <div className="fs-0">{getStatusBadge(request.status)}</div>
            </div>
          </div>

          {/* Requester Information */}
          <div className="row mb-3">
            <div className="col-12">
              <h6 className="text-primary mb-2 border-bottom pb-2">
                Requester Information
              </h6>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Requested By</label>
              <div className="fs-7 fw-semibold">
                {request.user_name || 'Unknown'}
              </div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Department</label>
              <div className="fs-7">{request.departmentName || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Company</label>
              <div className="fs-7">{request.company_name || 'N/A'}</div>
            </div>
          </div>

          {/* Request Details */}
          <div className="row mb-3">
            <div className="col-12">
              <h6 className="text-primary mb-2 border-bottom pb-2">
                Request Details
              </h6>
            </div>
            <div className="col-12 mb-2">
              <label className="form-label text-700 mb-1">Request Notes</label>
              <div className="fs-7 bg-light p-2 rounded">
                {request.request_note || 'No notes provided'}
              </div>
            </div>
          </div>

          {/* Timeline Information */}
          <div className="row">
            <div className="col-12">
              <h6 className="text-primary mb-2 border-bottom pb-2">Timeline</h6>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Requested On</label>
              <div className="fs-7">{formatDate(request.created_at)}</div>
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label text-700 mb-1">Last Updated</label>
              <div className="fs-7">{formatDate(request.updated_at)}</div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button
          variant="phoenix-secondary"
          onClick={handleClose}
          className="px-4"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewPendingApprovalModal;
